import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    ChevronLeft, ChevronRight, Plus, X, CalendarClock, Trash2,
    AlertTriangle, CheckCircle2, Clock, Ban,
} from "lucide-react";

const MONTH_LABELS = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const WEEK_LABELS = ["L", "M", "M", "J", "V", "S", "D"];

// ---- Helpers ----
function ymKey(y, m) {
    return `${y}-${String(m + 1).padStart(2, "0")}`;
}
function iso(y, m, d) {
    return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function firstDayOfWeek(y, m) {
    // Monday = 0 for our grid
    const raw = new Date(y, m, 1).getDay(); // 0=Sunday
    return (raw + 6) % 7;
}
function daysInMonth(y, m) {
    return new Date(y, m + 1, 0).getDate();
}

/**
 * Candidate agenda view (spec §6).
 * - Monthly grid with confirmed missions (green), pending applications (amber),
 *   self-declared unavailabilities (grey striped) and overlap warnings (red pulse).
 * - Add / remove unavailabilities in-place.
 */
export default function CandidateAgenda() {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await api.get("/candidates/me/agenda", {
                params: { year, month: month + 1 },
            });
            setData(r.data);
        } catch (_) {
            setData(null);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, [year, month]);

    // Group events by date for quick day lookup
    const byDate = useMemo(() => {
        const map = new Map();
        if (!data) return map;
        for (const e of data.confirmed || []) {
            if (!map.has(e.date)) map.set(e.date, []);
            map.get(e.date).push({ ...e, kind: "confirmed" });
        }
        for (const e of data.pending || []) {
            if (!map.has(e.date)) map.set(e.date, []);
            map.get(e.date).push({ ...e, kind: "pending" });
        }
        for (const u of data.unavailabilities || []) {
            const s = new Date(u.date_start);
            const e = new Date(u.date_end);
            for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
                const key = d.toISOString().slice(0, 10);
                if (!map.has(key)) map.set(key, []);
                map.get(key).push({ ...u, kind: "unavailable" });
            }
        }
        return map;
    }, [data]);

    const conflictSet = useMemo(() => new Set(data?.conflicts || []), [data]);

    const removeUnav = async (id) => {
        try {
            await api.delete(`/candidates/me/unavailabilities/${id}`);
            toast.success("Indisponibilité retirée");
            refresh();
        } catch (_) {
            toast.error("Erreur");
        }
    };

    // Build the calendar grid (weeks × 7 days), aligned to Monday
    const grid = useMemo(() => {
        const offset = firstDayOfWeek(year, month);
        const total = daysInMonth(year, month);
        const cells = [];
        for (let i = 0; i < offset; i++) cells.push(null);
        for (let d = 1; d <= total; d++) cells.push(d);
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, [year, month]);

    const prevMonth = () => {
        if (month === 0) { setYear(y => y - 1); setMonth(11); }
        else setMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (month === 11) { setYear(y => y + 1); setMonth(0); }
        else setMonth(m => m + 1);
    };

    const todayIso = new Date().toISOString().slice(0, 10);

    return (
        <section className="sd-card p-6" data-testid="candidate-agenda-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
                <div className="flex items-center gap-3">
                    <button
                        onClick={prevMonth}
                        className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
                        data-testid="agenda-prev-month"
                        aria-label="Mois précédent"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <div className="font-display text-xl text-[#0B192C]">
                        {MONTH_LABELS[month]} {year}
                    </div>
                    <button
                        onClick={nextMonth}
                        className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
                        data-testid="agenda-next-month"
                        aria-label="Mois suivant"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    <Legend color="bg-emerald-500" label="Missions confirmées" icon={<CheckCircle2 size={11} />} />
                    <Legend color="bg-[#F5A623]" label="Candidatures" icon={<Clock size={11} />} />
                    <Legend color="bg-slate-400" label="Indisponible" icon={<Ban size={11} />} />
                    <Legend color="bg-red-500 animate-pulse" label="Conflit" icon={<AlertTriangle size={11} />} />
                    <button
                        onClick={() => { setShowAdd(true); setSelectedDay(null); }}
                        className="btn-gold text-xs inline-flex items-center gap-1 ml-2"
                        data-testid="agenda-add-unavail"
                    >
                        <Plus size={12} /> Bloquer une période
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
                {WEEK_LABELS.map((w, i) => (
                    <div
                        key={i}
                        className="text-center text-[11px] font-bold text-slate-400 uppercase pb-1"
                    >
                        {w}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
                {loading && (
                    <div className="col-span-7 text-center py-16 text-slate-400 text-sm">
                        Chargement de l'agenda…
                    </div>
                )}
                {!loading && grid.map((d, i) => {
                    if (d == null) return <div key={i} className="aspect-square" />;
                    const date = iso(year, month, d);
                    const items = byDate.get(date) || [];
                    const isConflict = conflictSet.has(date);
                    const isToday = date === todayIso;
                    const isPast = date < todayIso;
                    return (
                        <button
                            key={i}
                            onClick={() => { setSelectedDay(date); if (items.length === 0) setShowAdd(true); }}
                            className={`aspect-square rounded-lg p-1 text-left flex flex-col text-[11px] transition border ${
                                isConflict
                                    ? "border-red-400 bg-red-50 animate-pulse"
                                    : isToday
                                    ? "border-[#F5A623] bg-[#F5A623]/5 shadow-sm"
                                    : isPast
                                    ? "border-slate-100 bg-slate-50/50 text-slate-400"
                                    : "border-slate-200 bg-white hover:border-[#0B192C]"
                            }`}
                            data-testid={`agenda-day-${date}`}
                        >
                            <span className={`text-xs font-bold self-end ${isToday ? "text-[#F5A623]" : ""}`}>
                                {d}
                            </span>
                            <div className="flex-1 mt-1 space-y-0.5 overflow-hidden w-full">
                                {items.slice(0, 2).map((ev, idx) => (
                                    <div
                                        key={idx}
                                        className={`truncate rounded px-1 py-0.5 text-white text-[9px] ${
                                            ev.kind === "confirmed"
                                                ? "bg-emerald-500"
                                                : ev.kind === "pending"
                                                ? "bg-[#F5A623] text-[#0B192C]"
                                                : "bg-slate-400"
                                        }`}
                                        title={ev.title || ev.reason}
                                    >
                                        {ev.kind === "unavailable" ? "Indispo." : ev.title}
                                    </div>
                                ))}
                                {items.length > 2 && (
                                    <div className="text-[9px] text-slate-500">
                                        +{items.length - 2}
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Selected day details */}
            {selectedDay && byDate.get(selectedDay)?.length > 0 && (
                <div className="mt-5 rounded-xl border border-slate-200 p-4 bg-slate-50">
                    <div className="text-xs text-slate-500 mb-2 flex items-center justify-between">
                        <span>
                            <CalendarClock size={12} className="inline -mt-0.5 mr-1" />
                            {new Date(selectedDay).toLocaleDateString("fr-FR", {
                                weekday: "long", day: "numeric", month: "long", year: "numeric",
                            })}
                        </span>
                        {conflictSet.has(selectedDay) && (
                            <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                                <AlertTriangle size={12} /> Conflit détecté
                            </span>
                        )}
                    </div>
                    <div className="space-y-2">
                        {byDate.get(selectedDay).map((ev, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between gap-2 bg-white rounded-lg p-3 border border-slate-200"
                            >
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-[#0B192C]">
                                        {ev.kind === "unavailable"
                                            ? `Indisponibilité — ${ev.reason || "sans motif"}`
                                            : ev.title}
                                    </div>
                                    {ev.kind !== "unavailable" && (
                                        <div className="text-[11px] text-slate-500">
                                            {ev.city} · {ev.start_time}–{ev.end_time}
                                            {ev.hourly_gross > 0 && ` · ${ev.hourly_gross.toFixed(2)} €/h HT`}
                                        </div>
                                    )}
                                    {ev.kind === "unavailable" && ev.date_start !== ev.date_end && (
                                        <div className="text-[11px] text-slate-500">
                                            Du {ev.date_start} au {ev.date_end}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`pill text-[10px] ${
                                            ev.kind === "confirmed"
                                                ? "pill-green"
                                                : ev.kind === "pending"
                                                ? "pill-gold"
                                                : "pill-slate"
                                        }`}
                                    >
                                        {ev.kind === "confirmed"
                                            ? "Confirmé"
                                            : ev.kind === "pending"
                                            ? "En attente"
                                            : "Indispo."}
                                    </span>
                                    {ev.kind === "unavailable" && ev.id && (
                                        <button
                                            onClick={() => removeUnav(ev.id)}
                                            className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50"
                                            title="Retirer cette indisponibilité"
                                            data-testid={`agenda-remove-unav-${ev.id}`}
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showAdd && (
                <AddUnavailabilityModal
                    initialDate={selectedDay}
                    onClose={() => setShowAdd(false)}
                    onSaved={() => { setShowAdd(false); refresh(); }}
                />
            )}
        </section>
    );
}

function Legend({ color, label, icon }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-slate-500">
            <span className={`inline-flex items-center justify-center w-4 h-4 rounded ${color} text-white`}>
                {icon}
            </span>
            {label}
        </span>
    );
}

function AddUnavailabilityModal({ initialDate, onClose, onSaved }) {
    const today = new Date().toISOString().slice(0, 10);
    const [dateStart, setDateStart] = useState(initialDate || today);
    const [dateEnd, setDateEnd] = useState(initialDate || today);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!dateStart) return;
        setSaving(true);
        try {
            const r = await api.post("/candidates/me/unavailabilities", {
                date_start: dateStart,
                date_end: dateEnd || dateStart,
                reason,
            });
            if (r.data.warning) {
                toast.warning(r.data.warning);
            } else {
                toast.success("Indisponibilité ajoutée");
            }
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-auto py-8 px-4"
            onClick={onClose}
            data-testid="agenda-unavail-modal"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center px-6 py-5 border-b border-slate-200">
                    <h2 className="font-display text-xl text-[#0B192C]">Bloquer une période</h2>
                    <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
                        <X size={18} />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Du
                            </label>
                            <input
                                type="date"
                                value={dateStart}
                                onChange={(e) => setDateStart(e.target.value)}
                                min={today}
                                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#F5A623] outline-none"
                                data-testid="unavail-date-start"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Au
                            </label>
                            <input
                                type="date"
                                value={dateEnd}
                                onChange={(e) => setDateEnd(e.target.value)}
                                min={dateStart}
                                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#F5A623] outline-none"
                                data-testid="unavail-date-end"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                            Motif (facultatif)
                        </label>
                        <input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Ex. vacances, examen, engagement personnel…"
                            maxLength={200}
                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#F5A623] outline-none"
                            data-testid="unavail-reason"
                        />
                    </div>
                    <p className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 border-l-2 border-[#F5A623]">
                        Vous ne recevrez plus de nouvelles missions sur cette période. Les missions
                        déjà confirmées ne sont pas annulées automatiquement — contactez-nous
                        en cas d'empêchement.
                    </p>
                </div>
                <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
                    <button onClick={onClose} className="btn-ghost-navy text-sm">Annuler</button>
                    <button
                        onClick={save}
                        disabled={saving || !dateStart}
                        className="btn-gold text-sm disabled:opacity-50"
                        data-testid="unavail-save-btn"
                    >
                        {saving ? "Enregistrement…" : "Bloquer"}
                    </button>
                </div>
            </div>
        </div>
    );
}
